import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useActiveOrganization } from '@repo/api-client/react';
import { useInvoice, useUpdateInvoice } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { AiPill } from '@/features/ai/AiPill';
import { useAiComposer } from '@/features/ai/AiComposerContext';
import { InvoiceForm } from '@/features/invoice/InvoiceForm';
import { invoiceFormFromWire } from '@/features/invoice/invoiceFormSchema';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function InvoiceEditScreen() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { canEditOrg } = useActiveRole();
  const org = useActiveOrganization();
  const { invoice, loading, error } = useInvoice(invoiceId ?? '');
  const { updateInvoice, loading: saving } = useUpdateInvoice();
  const { toast } = useToast();
  const { open: openAi } = useAiComposer();

  if (loading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <div className="mt-6 grid gap-4">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton key={idx} className="h-24 rounded-xl" />
          ))}
        </div>
      </Page>
    );
  }

  if (!canEditOrg) {
    return (
      <Navigate
        to={invoiceId ? `/invoices/${invoiceId}` : '/invoices'}
        replace
      />
    );
  }

  if (error || !invoice) {
    return (
      <Page>
        <Alert variant="danger">
          {error ? describeAppError(error).message : 'Invoice not found.'}
        </Alert>
      </Page>
    );
  }

  if (invoice.status !== 'draft') {
    return (
      <Page>
        <PageHeader
          title={`Edit ${invoice.invoiceNumber}`}
          description="This invoice has been issued and is read-only. To make changes, duplicate it as a new draft."
        />
        <Alert variant="danger">
          Only drafts can be edited. Use Duplicate from the invoice menu to start a new revision.
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={`Edit ${invoice.invoiceNumber}`} />

      <AiPill className="mb-5" onClick={() => openAi()} />

      <InvoiceForm
        defaultCurrency={invoice.currency || org?.currency || 'USD'}
        defaultValues={invoiceFormFromWire(invoice)}
        invoiceNumber={invoice.invoiceNumber}
        loading={saving}
        submitLabel="Save changes"
        onCancel={() => navigate(`/invoices/${invoice.id}`)}
        onSubmit={async (payload) => {
          if (!org) return;
          try {
            await updateInvoice({
              organizationId: org.id,
              id: invoice.id,
              ...payload,
            });
            toast({
              title: 'Invoice saved',
              description: `${invoice.invoiceNumber} updated.`,
              variant: 'success',
            });
            navigate(`/invoices/${invoice.id}`);
          } catch (err) {
            const desc = describeAppError(err);
            toast({ title: desc.title, description: desc.message, variant: 'danger' });
          }
        }}
      />
    </Page>
  );
}
